// The Patient Board (Blueprint §2 screen 3; parity F-BOARD-01…05). Ports the
// SEMANTICS of legacy loadPipeline / renderPipeline / shaChip / openSetStage /
// execSetStage (v20 lines ~1655-1692, 3658, 4091-4123) onto the 2.0 data layer
// (data/cases.js — the SINGLE cases writer), the store and the delegated
// data-action router.
//
// On screen (left → right, one column per pipeline stage):
//   Triage · Enrolled · Welcome Call · SHA Pending · SHA Approved · Delivery ·
//   On Therapy · Refill Due — each a labelled column with a live count, a stage
//   swatch, and its cards. Triage (the first column) carries the
//   "(new patients start here)" hint. Closed/archived/deceased cases are never
//   on the board (groupByStage skips them). The whole strip is ONE focusable,
//   aria-labelled, horizontally-scrollable region (WCAG 2.1.1 + region label).
//
// Each card is a real button (Enter/Space via [data-kbd-click]) that opens the
// patient workspace, plus a "Set stage" control that opens the stage picker →
// rpc_set_stage (UNTOUCHED, byte-identical to v_20) → VERIFIED reload: the move
// is only reported "done" once the reloaded case actually reads the new enum; a
// failure is surfaced and the modal keeps its state.
//
// Scope: the board shows only cases inside the nurse's chosen queue scope
// (F-SCOPE), reusing data/scope.js. A programme filter shows the "Showing X
// only" banner. Realtime (SLICES.RT_CHANGES 'pipeline'/'patients') reloads
// through the single cases writer, never a second capped query.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { openModal, closeModal, setBtnBusy } from '../ui/modal.js';
import { netErr } from '../errors.js';
import { STAGES, resolveStage } from '../db/contract.js';
import { registerView } from './registry.js';
import { initials } from './shell.js';
import { progDotHTML, programmeBannerCopy, pillLabel } from '../programmes.js';
import { getScope, scopeMatch, scopeLabel } from '../data/scope.js';
import { openWorkspace } from './workspace.js';
import { loadWorkspace } from '../data/workspace.js';
import {
  loadAllCases,
  forceReloadAllCases,
  loadAEFlags,
  allCases,
  casesLoaded,
  aeFlags,
  aeFlagsStale,
  groupByStage,
  slaBadge,
  shaMeta,
  setStage,
} from '../data/cases.js';

// ─── SVG icons (no functional emoji per §3; inline, CSP-safe) ───────────────

/** @param {string} paths @param {number} [size] @returns {string} */
function ic(paths, size = 16) {
  return (
    `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}
const IC_WARN = '<path d="M12 4L2.5 20h19z"/><path d="M12 10v4m0 3v.5"/>';
const IC_SHUFFLE = '<path d="M16 3h5v5"/><path d="M21 3l-7 7"/><path d="M8 21H3v-5"/><path d="M3 21l7-7"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M3 3l6 6"/>';

/** Deterministic avatar colour from a name (self-contained port of legacy ac()). */
function avatarColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 42% 42%)`;
}

// ─── Pure presentation helpers (unit-tested) ───────────────────────────────

/**
 * The AE chip for a card, given its flag (legacy getAEBadge). A serious AE is a
 * loud danger chip, a non-serious open AE a quieter warn chip, none → ''.
 * @param {{hasOpenAE?: boolean, isSeriousAE?: boolean}|undefined} f
 * @returns {string}
 */
export function aeChipHTML(f) {
  if (!f) return '';
  if (f.isSeriousAE) return `<span class="bc-chip ae-serious">${ic(IC_WARN, 11)}Serious AE</span>`;
  if (f.hasOpenAE) return '<span class="bc-chip ae-open">AE open</span>';
  return '';
}

/**
 * The SHA chip for a card (legacy shaChip): hidden for blank/not_started so
 * rows without SHA activity stay uncluttered; otherwise a status-coloured chip.
 * @param {string|null|undefined} status cases.sha_status
 * @returns {string}
 */
export function shaChipHTML(status) {
  const m = shaMeta(status);
  if (m.empty) return '';
  return `<span class="bc-chip sha ${esc(m.cls)}" title="${esc('SHA: ' + m.label)}">SHA: ${esc(m.label)}</span>`;
}

/**
 * Per-column copy, keyed off the STAGE KEY — never the ordinal (legacy
 * v20:1670,1673). `promote_consent_to_case` writes current_stage =
 * 'welcome_call_due', which STAGE_ENUM_MAP puts at index 1 = ENROLLED, so that
 * is where a newly promoted patient actually appears. Triage (index 0) is the
 * manual-correction bucket; telling a nurse new patients start there sends them
 * to the wrong column for a just-approved consent. Pure.
 * @param {string} stageKey a STAGES display key
 * @returns {{hint: string, empty: string}}
 */
export function columnCopy(stageKey) {
  const isEnrolled = stageKey === 'enrolled';
  return {
    hint: isEnrolled ? '<span class="bcol-hint">(new patients start here)</span>' : '',
    empty: isEnrolled ? 'Newly accepted patients land here.' : 'No patients',
  };
}

/**
 * The board meta line (legacy pipe-meta): how many in-scope cases show, out of
 * the full set, plus stage count / programme / scope context. Pure.
 * @param {{inScope: number, total: number, scopeMode: string, scopeText: string, programmeName: string|null}} ctx
 * @returns {string}
 */
export function boardMeta(ctx) {
  const of = ctx.inScope !== ctx.total ? ` of ${ctx.total}` : '';
  const prog = ctx.programmeName ? ` · ${ctx.programmeName}` : '';
  const scope = ctx.scopeMode !== 'all' ? ` · scope: ${ctx.scopeText}` : '';
  return `${ctx.inScope}${of} patient${ctx.inScope === 1 ? '' : 's'} · ${STAGES.length} stages${prog}${scope}`;
}

// ─── Module state ──────────────────────────────────────────────────────────

let _wired = false;
let _setStageCase = /** @type {string|null} */ (null); // case id the picker is acting on

/** Test-only: reset module state. @returns {void} */
export function _resetBoard() {
  _wired = false;
  _setStageCase = null;
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}

function boardMounted() {
  const d = doc();
  const ui = store.get(SLICES.UI) || {};
  return !!d && ui.view === 'board' && !!d.getElementById('board-view');
}

function session() {
  return store.get(SLICES.SESSION) || {};
}
function selfId() {
  const s = session();
  return s.user && s.user.id;
}
function role() {
  return session().role;
}
function scope() {
  return getScope(selfId(), role());
}

/** A programme code's human display name (falls back to the code). @param {string} code @returns {string} */
function programmeLabel(code) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  return pillLabel(code, prog.byCode || {});
}

/** Look up a programme's validated accent hex from the loaded catalogue. */
function programmeHex(code) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const p = prog.byCode && prog.byCode[code];
  return p && p.brand_accent_hex;
}

// ─── Renderers (store → DOM) ───────────────────────────────────────────────

/** Re-render the whole board from the PATIENTS slice. */
function renderBoard() {
  if (!boardMounted()) return;
  const d = doc();
  const host = d.getElementById('board-cols');
  if (!host) return;
  const slice = store.get(SLICES.PATIENTS) || {};

  // Load error → a single honest retry, never a false empty board.
  const meta = d.getElementById('board-meta');
  if (slice.loadError) {
    host.innerHTML =
      '<button type="button" class="board-error" data-action="board-retry">' +
      ic(IC_WARN, 22) +
      '<div><h2>Couldn’t load the board — tap to retry</h2>' +
      '<p>The query failed or was denied. Nothing is hidden — tap to try again.</p></div>' +
      '</button>';
    if (meta) meta.textContent = '';
    renderBanner();
    return;
  }

  // FIRST LOAD STILL IN FLIGHT — leave the skeleton columns alone. Painting the
  // empty board here would show eight zero-count columns, which reads as "this
  // programme has no patients" rather than "still loading".
  if (!casesLoaded()) {
    if (meta) meta.textContent = 'Loading patients…';
    renderBanner();
    return;
  }

  const cases = allCases();
  const inScope = cases.filter((c) => scopeMatch(c.assigned_to || null, scope(), selfId()));
  const { groups, counts, total: shown } = groupByStage(inScope);
  const flags = aeFlags();
  const now = new Date();

  host.innerHTML = STAGES.map((s) => {
    const bucket = groups[s.key] || [];
    const copy = columnCopy(s.key);
    const hint = copy.hint;
    const cards = bucket.length
      ? bucket.map((c) => cardHTML(c, s.key, flags, now)).join('')
      : `<div class="bcol-empty">${copy.empty}</div>`;
    return (
      `<section class="bcol" aria-label="${esc(s.l + ' — ' + counts[s.key] + ' patient' + (counts[s.key] === 1 ? '' : 's'))}">` +
      '<header class="bcol-hd">' +
      `<span class="bcol-t"><span class="bcol-sw" data-swatch="${esc(s.c)}" aria-hidden="true"></span>${esc(s.s)}${hint}</span>` +
      `<span class="bcol-c">${esc(String(counts[s.key]))}</span>` +
      '</header>' +
      `<div class="bcol-bd">${cards}</div>` +
      '</section>'
    );
  }).join('');

  // Stage swatches + avatar colours via the CSSOM (an inline style= attribute
  // would be blocked by CSP style-src 'self'; a scripted .style mutation isn't).
  host.querySelectorAll('.bcol-sw[data-swatch]').forEach((el) => {
    /** @type {any} */ (el).style.background = el.getAttribute('data-swatch');
  });
  host.querySelectorAll('.bc-av[data-avatar]').forEach((el) => {
    /** @type {any} */ (el).style.background = el.getAttribute('data-avatar');
  });

  // AE read failed → say so. Silently dropping "Serious AE" chips is a false
  // all-clear on the most safety-critical signal this board carries.
  const aeNotice = d.getElementById('board-ae-notice');
  if (aeNotice) {
    const degraded = aeFlagsStale();
    aeNotice.hidden = !degraded;
    aeNotice.textContent = degraded
      ? 'Adverse-event flags could not be loaded — AE chips may be missing from these cards.'
      : '';
  }

  if (meta) {
    const prog = store.get(SLICES.PROGRAMMES) || {};
    const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';
    meta.textContent = boardMeta({
      inScope: shown,
      total: groupByStage(cases).total,
      scopeMode: scope().mode,
      scopeText: scopeLabel(scope()),
      programmeName: active !== 'all' ? pillLabel(active, prog.byCode || {}) : null,
    });
  }
  renderBanner();
}

/** One board card. @param {any} c case row @param {string} stageKey @param {Record<string,any>} flags @param {Date} now */
function cardHTML(c, stageKey, flags, now) {
  const name = c.patient_name || '—';
  const caseId = c.case_id;
  const sla = slaBadge(c.created_at, stageKey, now);
  const f = flags[caseId];
  const serious = !!(f && f.isSeriousAE);
  const openAE = !!(f && f.hasOpenAE);
  const cls =
    'bcard' + (serious ? ' ae-serious' : openAE ? ' ae-open' : sla.cls === 'over' ? ' overdue' : '');
  // The programme DOT's label is an accessible name + tooltip — it must be the
  // human display name, never the raw code (progDotHTML documents param 1 as
  // the display name).
  const dot = c.programme_code
    ? progDotHTML(programmeLabel(c.programme_code), programmeHex(c.programme_code), 8)
    : '';
  const facility = c.treating_facility || c.facility_name || c.county || 'No facility';
  const slaB =
    sla.cls === 'age'
      ? `<span class="bc-age">${esc(sla.label)}</span>`
      : `<span class="bc-chip sla-${sla.cls}">${esc(sla.label)}</span>`;
  const risk = c.risk_level === 'High' ? '<span class="bc-chip risk">High</span>' : '';

  // NO NESTED INTERACTIVES: the card itself is a plain container. The open-
  // workspace target is the name/avatar block (its own role=button), and
  // "Set stage" is a SIBLING real <button>. Nesting a <button> inside a
  // role="button" both breaks keyboard activation (the keydown handler walked
  // up from the inner button to the card and opened the workspace instead) and
  // is invalid ARIA that hides the inner control from screen readers. This is
  // the same shape patients.js already uses (.pts-pc + a sibling .pts-acts).
  return (
    `<div class="${cls}">` +
    '<div class="bc-top">' +
    `<div class="bc-pc" role="button" tabindex="0" data-kbd-click data-action="board-open" ` +
    `data-case="${esc(caseId)}" aria-label="${esc(name + ' — open workspace')}">` +
    `<div class="bc-av" aria-hidden="true" data-avatar="${esc(avatarColor(name))}">${esc(initials(name))}</div>` +
    '<div class="bc-id">' +
    `<div class="bc-nm">${dot}<span>${esc(name)}</span></div>` +
    `<div class="bc-rf">${esc(c.reference_number || '—')}</div>` +
    '</div>' +
    '</div>' +
    `<button type="button" class="bc-move" data-action="board-setstage" data-case="${esc(caseId)}" ` +
    `aria-label="${esc('Set stage for ' + name)}" title="Set stage">${ic(IC_SHUFFLE, 14)}</button>` +
    '</div>' +
    `<div class="bc-loc">${esc(facility)}</div>` +
    `<div class="bc-ft">${slaB}${aeChipHTML(f)}${shaChipHTML(c.sha_status)}${risk}</div>` +
    '</div>'
  );
}

function renderBanner() {
  const d = doc();
  const el = d && d.getElementById('board-banner');
  if (!el) return;
  const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';
  if (active === 'all') {
    el.hidden = true;
    el.innerHTML = '';
    return;
  }
  const prog = store.get(SLICES.PROGRAMMES) || {};
  el.hidden = false;
  el.innerHTML = programmeBannerCopy(pillLabel(active, prog.byCode || {}), 'patients');
}

// ─── Set-stage picker (legacy openSetStage / execSetStage) ─────────────────

/**
 * Open the stage picker for a case (legacy openSetStage). Pre-selects the
 * case's current stage; requires no reason (defaults to 'Manual stage change'
 * like v_20). @param {string} caseId
 */
function openSetStage(caseId) {
  const c = allCases().find((x) => x.case_id === caseId);
  if (!c) {
    toast('That patient is no longer on the board — refreshing', 'err');
    void refresh();
    return;
  }
  _setStageCase = caseId;
  const current = resolveStage(c);
  const opts = STAGES.map(
    (s, i) => `<option value="${esc(s.key)}"${i === current ? ' selected' : ''}>${esc(s.l)}</option>`
  ).join('');
  const body =
    '<p class="board-ss-lead">Move this patient to a different stage. Use for triage placement or corrections.</p>' +
    '<p class="board-ss-note">Stages normally advance automatically when you complete tasks — use this only for corrections.</p>' +
    '<div class="field"><label>New stage</label>' +
    `<select id="board-ss-stage">${opts}</select></div>` +
    '<div class="field"><label>Reason</label>' +
    '<textarea id="board-ss-reason" rows="3" placeholder="Why are you moving this patient?"></textarea></div>';
  openModal('Set pipeline stage', c.patient_name || '', body, [
    { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
    { l: 'Move patient', v: 'pri', k: 'move', fn: () => void submitSetStage() },
  ]);
}

/**
 * Submit the stage move: rpc_set_stage (untouched) → VERIFIED reload. The move
 * is reported "done" only once the reloaded case actually reads the new enum; a
 * driver failure keeps the modal open with the typed reason (setBtnBusy), and a
 * reload that didn't land the new stage is surfaced honestly.
 */
async function submitSetStage() {
  const d = doc();
  const caseId = _setStageCase;
  if (!caseId) return;
  const sel = d && d.getElementById('board-ss-stage');
  const stageKey = sel ? sel.value : '';
  const stageDef = STAGES.find((s) => s.key === stageKey);
  if (!stageKey || !stageDef) {
    toast('Choose a stage', 'err');
    return;
  }
  const reason = (d && d.getElementById('board-ss-reason') && d.getElementById('board-ss-reason').value) || '';

  setBtnBusy(true, 'Moving…', 'move');
  const res = await setStage(caseId, stageKey, reason);
  if (!res.ok) {
    // Keep the modal + typed reason (KEEP-OPEN-ON-FAIL); surface honestly.
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not move patient'), 'err');
    return;
  }

  // Verified reload: pull a GUARANTEED-FRESH set and confirm the case landed on
  // the new enum before we claim success (legacy execSetStage's post-reload
  // guard). forceReloadAllCases, NOT loadAllCases — the shared single-flight
  // would hand us a snapshot taken before rpc_set_stage returned, and we'd warn
  // "the board didn't confirm" on a move that saved cleanly.
  await forceReloadAllCases();
  const moved = allCases().find((x) => x.case_id === caseId);
  closeModal();
  _setStageCase = null;
  // The picker can be opened from the patient WORKSPACE (its Re-route button)
  // — refresh the open workspace so its stage rail reflects the move, instead
  // of showing the pre-move chips until the next open.
  const ws = store.get(SLICES.WORKSPACE) || {};
  if (ws.caseId === caseId && ws.case) void loadWorkspace(caseId);
  if (moved && moved.current_stage === res.data.stageEnum) {
    toast('Patient moved to ' + stageDef.l, 'ok');
  } else {
    // The write returned clean but the reload didn't reflect it (RLS, a racing
    // change, or a stale replica) — say so, don't pretend it worked.
    toast('Move saved, but the board didn’t confirm the new stage — refresh to check', 'err', 10000);
  }
  renderBoard();
}

// ─── Actions ────────────────────────────────────────────────────────────────

/** Reload AE flags + the full case set, then re-render. Never throws. */
async function refresh() {
  // Returned so Sync sees a failure of EITHER stream — see registry.firstFailure().
  const res = await Promise.all([loadAEFlags(), loadAllCases()]);
  renderBoard();
  return res;
}

// ─── Wiring (once) ──────────────────────────────────────────────────────────

function wireOnce() {
  if (_wired) return;
  _wired = true;

  // Re-render whenever the shared cases slice changes (loadAllCases / loadAEFlags).
  store.subscribe(SLICES.PATIENTS, () => renderBoard());
  // Programme filter change (banner + a re-scoped reload).
  store.subscribe(SLICES.FILTERS, () => {
    if (boardMounted()) void refresh();
  });
  // Realtime: the board owns the 'pipeline' key (cases changes fan out to it) —
  // reload through the SINGLE cases writer, never a second capped query.
  store.subscribe(SLICES.RT_CHANGES, (v) => {
    if (boardMounted() && v && Array.isArray(v.changed) && (v.changed.includes('pipeline') || v.changed.includes('patients'))) {
      void refresh();
    }
  });
}

/** @param {string} caseId */
function openWorkspaceSafe(caseId) {
  return openWorkspace(caseId);
}

// ─── View registration ──────────────────────────────────────────────────────

/**
 * The board's static skeleton: a header (title + meta + programme banner) and
 * the ONE focusable, aria-labelled, horizontally-scrollable columns region
 * (WCAG 2.1.1 keyboard scroll + a name for the region).
 * @returns {string}
 */
function skeleton() {
  return (
    '<div id="board-view" class="board">' +
    '<header class="board-head">' +
    '<div><h1 class="board-h">Patient board</h1>' +
    '<p id="board-meta" class="board-meta"></p></div>' +
    '</header>' +
    '<div id="board-banner" class="board-banner" hidden></div>' +
    // Degraded-state notice: shown only when the adverse-event read failed, so
    // missing AE chips are never mistaken for "no adverse events".
    '<div id="board-ae-notice" class="board-ae-notice" role="status" hidden></div>' +
    '<div id="board-cols" class="board-cols" role="region" aria-label="Patient pipeline — scroll horizontally through the stages" tabindex="0">' +
    STAGES.map(
      (s) =>
        `<section class="bcol"><header class="bcol-hd"><span class="bcol-t">${esc(s.s)}</span><span class="bcol-c">–</span></header>` +
        '<div class="bcol-bd"><div class="skel"></div><div class="skel"></div></div></section>'
    ).join('') +
    '</div>' +
    '</div>'
  );
}

/**
 * Register the Board view, replacing the Phase-1 placeholder. Call once at boot
 * from main.js (and the dev harness).
 * @returns {void}
 */
export function registerBoard() {
  // Action handlers register at BOOT, not on first board render: the patient
  // workspace's "Re-route / Set stage" button fires 'board-setstage' from any
  // view (found dead in live QC when Legacy was visited before the Board).
  register('board-retry', () => void refresh());
  register('board-open', (el) => {
    const caseId = el.getAttribute('data-case');
    if (caseId) void openWorkspaceSafe(caseId);
  });
  register('board-setstage', (el) => {
    const caseId = el.getAttribute('data-case');
    if (caseId) openSetStage(caseId);
  });
  registerView('board', {
    render(host) {
      host.innerHTML = skeleton();
      wireOnce();
      renderBoard(); // first paint from whatever is already in the store
    },
    load() {
      return refresh();
    },
  });
}
